import React, { useEffect, useMemo, useRef } from 'react';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';

import ChatArea from '../components/Chat/ChatArea';
import { useChatContext } from '../context/ChatContext';
import { useFileTreeContext } from '../context/FileTreeContext';
import apiService from '../services/api';
import type { AppShellOutletContext } from '../components/AppShell/AppShell';

/**
 * Route component for `/c/*` and `/p/:projectId/c/*`.
 *
 * URL ↔ chat session bridge:
 *  - `/c/new` (or `/p/:id/c/new`) → start a fresh chat (clearChat)
 *  - `/c/:chatId` (or `/p/:id/c/:chatId`) → load that session's messages
 *    into the ChatContext so the next composer send sticks to that session
 *
 * Project context still routes through props on ChatArea — the in-chat
 * project chip + onClearProject flow is unchanged.
 */
const ChatPage: React.FC = () => {
  const navigate = useNavigate();
  const params = useParams<{ projectId?: string; chatId?: string }>();
  const { projects, refreshProjects } = useOutletContext<AppShellOutletContext>();
  const { setMessages, setCurrentSessionId, clearChat } = useChatContext();
  const { setSessionSources } = useFileTreeContext();

  const activeProjectId = params.projectId ?? null;
  const activeProject = useMemo(
    () => (activeProjectId ? projects.find((p) => p.id === activeProjectId) ?? null : null),
    [activeProjectId, projects],
  );

  // Tracks the last chatId we loaded so we don't re-fetch on every render
  // and so a stale fetch can't clobber state when the user navigates fast.
  const loadedChatIdRef = useRef<string | null>(null);

  useEffect(() => {
    const chatId = params.chatId;

    // Brand-new chat — wipe whatever the previous route left behind, including
    // the previous chat's attached document sources.
    if (!chatId || chatId === 'new') {
      if (loadedChatIdRef.current !== null) {
        clearChat();
        setSessionSources([]);
        loadedChatIdRef.current = null;
      }
      return;
    }

    // Same session already loaded — nothing to do.
    if (loadedChatIdRef.current === chatId) return;

    loadedChatIdRef.current = chatId;
    let cancelled = false;
    apiService
      .getChatSessionMessages(chatId)
      .then((msgs) => {
        if (cancelled || loadedChatIdRef.current !== chatId) return;
        setMessages(msgs);
        setCurrentSessionId(chatId);
        // Restore the chat's document context. Each persisted message carries
        // the file_context ids it was sent with; their union is the set of
        // documents this conversation referenced, so reopening the chat keeps
        // those sources active for follow-up questions (and shows them in the
        // Sources panel) instead of starting from an empty context.
        const restored = new Set<string>();
        for (const m of msgs) {
          for (const fid of m.fileContext ?? []) restored.add(fid);
        }
        setSessionSources(restored);
      })
      .catch(() => {
        if (cancelled || loadedChatIdRef.current !== chatId) return;
        toast.error('טעינת השיחה נכשלה');
        navigate('/c/new', { replace: true });
      });
    return () => {
      cancelled = true;
    };
  }, [params.chatId, setMessages, setCurrentSessionId, setSessionSources, clearChat, navigate]);

  const handleClearProject = () => {
    // Clearing the project chip means leaving the project scope — drop the
    // /p/:projectId prefix and land on a fresh chat.
    navigate('/c/new');
  };

  return (
    <ChatArea
      activeProjectId={activeProjectId}
      activeProject={activeProject}
      onClearProject={handleClearProject}
      onProjectsRefresh={refreshProjects}
    />
  );
};

export default ChatPage;
